import { Account, Ed25519PrivateKey } from "@aptos-labs/ts-sdk";
import type {
  AptosSignMessageInput,
  AptosSignMessageOutput,
  InputTransactionData,
} from "@aptos-labs/wallet-adapter-core";
import {
  type SubAccountData,
  buildLinkProof,
  createMasterSignaturePayload,
  decodeHexToString,
  deriveSubAccountFromMasterSignature,
  deriveSubAccountsFromMasterSignature,
  parseSubAccountsData,
} from "@ekidenfi/ts-sdk";

import { submitTransaction } from "@utils/xchainTransaction";

import { aptosClient } from "@src/api/aptos";
import { getEkidenSdkClient, getVaultContract } from "@src/api/sdk";

import { wait } from "@utils/wait";
import type { SubAccount, Wallet } from "./types";

export class SubAccountService {
  private ownedSubAddressesCache = new Map<string, Promise<[string[]]>>();
  private subAccountsRawCache = new Map<string, SubAccountData>();
  private masterSignatureCache = new Map<string, string>();

  // Helper methods
  private normalizeAddress = (addr: string): string => {
    const hex = addr.startsWith("0x") ? addr.slice(2) : addr;
    return `0x${hex.padStart(64, "0")}`;
  };

  private safeDecodeType = (item: string): string => {
    if (item.startsWith("0x")) {
      return decodeHexToString(item);
    }
    return item;
  };

  private signatureToBytes = (signature: string | Uint8Array | { toUint8Array: () => Uint8Array }): Uint8Array => {
    if (typeof signature === "string") {
      return new Uint8Array(
        signature
          .replace("0x", "")
          .match(/.{1,2}/g)
          ?.map((byte: string) => Number.parseInt(byte, 16)) || [],
      );
    }
    if (signature instanceof Uint8Array) {
      return signature;
    }
    if ("toUint8Array" in signature && typeof signature.toUint8Array === "function") {
      return signature.toUint8Array();
    }
    throw new Error("Unknown signature format");
  };

  private extractPrivateKeyBytes = (
    signature: string | Uint8Array | { toUint8Array: () => Uint8Array },
  ): Uint8Array => {
    const bytes = this.signatureToBytes(signature);
    return bytes.slice(0, 32);
  };

  signMasterPayload = async (wallet: Wallet, version = 1): Promise<string | null> => {
    if (!wallet.account) return null;

    const address = wallet.account.address.toString();
    const cached = this.masterSignatureCache.get(address);
    if (cached) {
      return cached;
    }

    const masterPayload = createMasterSignaturePayload(address, version);
    const signResult = await this.signMessageWithFocus(wallet, masterPayload);

    const signatureHex =
      typeof signResult.signature === "string"
        ? signResult.signature
        : `0x${Array.from(this.signatureToBytes(signResult.signature))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("")}`;

    this.masterSignatureCache.set(address, signatureHex);
    return signatureHex;
  };

  getMasterSignature = (address: string): string | null => {
    return this.masterSignatureCache.get(address) || null;
  };

  signAccountsWithMasterSignature = async (
    wallet: Wallet,
  ): Promise<{ funding: SubAccount; trading: SubAccount } | null> => {
    if (!wallet.account) return null;

    const masterSignature = await this.signMasterPayload(wallet);
    if (!masterSignature) return null;

    const { funding, trading } = await deriveSubAccountsFromMasterSignature(masterSignature);

    return { funding, trading };
  };

  generateTradingAccountByNonceFromMaster = async (wallet: Wallet, nonce: string): Promise<SubAccount | null> => {
    if (!wallet.account) return null;

    const address = wallet.account.address.toString();
    let masterSignature: string | undefined = this.masterSignatureCache.get(address);

    if (!masterSignature) {
      const signed = await this.signMasterPayload(wallet);
      if (!signed) return null;
      masterSignature = signed;
    }

    return deriveSubAccountFromMasterSignature({
      masterSignature,
      type: "Trading",
      nonce,
    });
  };

  // Public API
  fetchOwnedSubAddresses = async (
    userAddress: string,
    forceRefresh = false,
    onFailure?: () => void,
  ): Promise<string[]> => {
    if (!userAddress || userAddress === "0x") return [];

    const cached = this.ownedSubAddressesCache.get(userAddress);
    if (cached && !forceRefresh) {
      const addresses = (await cached)?.[0] || [];
      const normalized = addresses.map((addr) => this.normalizeAddress(addr));
      return normalized;
    }

    const ownedSubAccsPayload = getEkidenSdkClient().vaultOnChain.ownedSubAccs({
      vaultAddress: getVaultContract(),
      userAddress,
    });
    const ownedSubAccsResult_ = this.viewWithRetry<[string[]]>(ownedSubAccsPayload as any, 3, onFailure);
    this.ownedSubAddressesCache.set(userAddress, ownedSubAccsResult_);

    try {
      const ownedSubAccsResult = await ownedSubAccsResult_;
      const addresses = (ownedSubAccsResult?.[0] as string[]) || [];
      return addresses.map((addr) => this.normalizeAddress(addr));
    } catch (error) {
      this.ownedSubAddressesCache.delete(userAddress);
      throw error;
    }
  };

  fetchSubAccountsRaw = async (
    subAddresses: string[],
    forceRefresh = false,
    onFailure?: () => void,
  ): Promise<SubAccountData> => {
    const cacheKey = [...subAddresses].sort().join(",");
    const cached = this.subAccountsRawCache.get(cacheKey);

    if (cached && !forceRefresh) {
      return cached;
    }

    const getSubAccsPayload = getEkidenSdkClient().vaultOnChain.getSubAccs({
      vaultAddress: getVaultContract(),
      subAddresses,
    });
    const result = await this.viewWithRetry<any[]>(getSubAccsPayload as any, 3, onFailure);
    const parsed = parseSubAccountsData(result);

    this.subAccountsRawCache.set(cacheKey, parsed);
    return parsed;
  };

  fetchSubAccountsFromAptos = async (rootAddress: string): Promise<SubAccount[]> => {
    const owned = await this.fetchOwnedSubAddresses(rootAddress);
    if (owned.length === 0) {
      return [];
    }

    const { types: typesRaw, subs, nonces, orderIndexes } = await this.fetchSubAccountsRaw(owned);
    const subAccounts: SubAccount[] = [];

    for (let i = 0; i < subs.length; i++) {
      const bcAddress = this.normalizeAddress(subs[i]);
      const typeArr = typesRaw[i] || [];
      const decodedType = Array.isArray(typeArr)
        ? typeArr.map(this.safeDecodeType).join("")
        : this.safeDecodeType(typeArr);
      const isFunding = decodedType.includes("Funding");
      const isTrading = decodedType.includes("CrossTrading");
      const nonceValue = String(nonces[i] || "0");
      const orderIndex = Number.parseInt(orderIndexes[i], 10);

      if (isFunding) {
        subAccounts.push({
          address: bcAddress,
          privateKey: "",
          publicKey: "",
          type: "funding",
          orderIndex,
          nonce: nonceValue,
        });
      } else if (isTrading) {
        subAccounts.push({
          address: bcAddress,
          privateKey: "",
          publicKey: "",
          type: "trading",
          nonce: nonceValue,
          orderIndex,
        });
      }
    }

    return subAccounts;
  };

  checkAccountsLinkedToBlockchain = async (
    userAddress: string,
    forceRefresh = false,
    onFailure?: () => void | Promise<void>,
  ): Promise<boolean> => {
    try {
      const ownedAddresses_ = forceRefresh ? undefined : this.ownedSubAddressesCache.get(userAddress);
      let ownedAddresses: string[] = [];
      if (ownedAddresses_) {
        ownedAddresses = (await ownedAddresses_)?.[0] || [];
        ownedAddresses = ownedAddresses.map((addr) => this.normalizeAddress(addr));
      }

      if (!ownedAddresses || ownedAddresses.length === 0) {
        ownedAddresses = await this.fetchOwnedSubAddresses(userAddress, forceRefresh, onFailure);
        if (ownedAddresses.length === 0) {
          return false;
        }
      }

      const cacheKey = [...ownedAddresses].sort().join(",");
      let cachedData = forceRefresh ? undefined : this.subAccountsRawCache.get(cacheKey);

      if (!cachedData) {
        cachedData = await this.fetchSubAccountsRaw(ownedAddresses, forceRefresh, onFailure);
      }

      const { types: typesRaw } = cachedData;

      const decodedTypes = Array.isArray(typesRaw)
        ? typesRaw.map((item) => (Array.isArray(item) ? item.map(this.safeDecodeType) : [this.safeDecodeType(item)]))
        : [];

      const hasFunding = decodedTypes.some((typeArr) => typeArr.some((t) => t.includes("Funding") || t === "Funding"));
      const hasTrading = decodedTypes.some((typeArr) =>
        typeArr.some((t) => t.includes("CrossTrading") || t === "CrossTrading"),
      );

      return hasFunding && hasTrading;
    } catch {
      return false;
    }
  };

  linkAccountsToBlockchain = async (
    wallet: Wallet,
    fundingSubAccount: SubAccount,
    tradingSubAccount: SubAccount,
  ): Promise<void> => {
    const address = wallet.account!.address.toString();

    const fundingPrivateKey = new Ed25519PrivateKey(fundingSubAccount.privateKey);
    const fundingAccount = Account.fromPrivateKey({ privateKey: fundingPrivateKey });

    const tradingPrivateKey = new Ed25519PrivateKey(tradingSubAccount.privateKey);
    const tradingAccount = Account.fromPrivateKey({ privateKey: tradingPrivateKey });

    const fundingLinkProof = buildLinkProof(
      fundingAccount.publicKey.toUint8Array(),
      address,
      fundingAccount.sign(address).toUint8Array(),
    );
    const tradingLinkProof = buildLinkProof(
      tradingAccount.publicKey.toUint8Array(),
      address,
      tradingAccount.sign(address).toUint8Array(),
    );

    const createUserPayload = getEkidenSdkClient().vaultOnChain.createEkidenUser({
      vaultAddress: getVaultContract(),
      fundingLinkProof,
      crossTradingLinkProof: tradingLinkProof,
    }) as InputTransactionData["data"];

    const txResponse = await submitTransaction({ wallet, data: createUserPayload });
    await aptosClient.waitForTransaction({ transactionHash: txResponse.hash });

    // Clear caches
    this.ownedSubAddressesCache.delete(address);
    this.subAccountsRawCache.clear();
  };

  addTradingAccount = async (wallet: Wallet, nonce: string): Promise<SubAccount> => {
    const address = wallet.account!.address.toString();

    const tradingSubAccountData = await this.generateTradingAccountByNonceFromMaster(wallet, nonce);
    if (!tradingSubAccountData) {
      throw new Error("Failed to generate trading account");
    }

    const tradingPrivateKeyEd25519 = new Ed25519PrivateKey(tradingSubAccountData.privateKey);
    const tradingAccount = Account.fromPrivateKey({ privateKey: tradingPrivateKeyEd25519 });

    const tradingLinkProof = buildLinkProof(
      tradingAccount.publicKey.toUint8Array(),
      address,
      tradingAccount.sign(address).toUint8Array(),
    );

    const createAndLinkPayload = getEkidenSdkClient().vaultOnChain.createAndLinkSubAccount({
      vaultAddress: getVaultContract(),
      linkProof: tradingLinkProof,
      subAccountType: "Cross",
    }) as InputTransactionData["data"];

    const txResponse = await submitTransaction({ wallet, data: createAndLinkPayload });
    await aptosClient.waitForTransaction({ transactionHash: txResponse.hash });

    return tradingSubAccountData;
  };

  private signMessageWithFocus = async (
    wallet: Wallet,
    messageData: AptosSignMessageInput,
  ): Promise<AptosSignMessageOutput> => {
    return wallet.signMessage(messageData);
  };

  loadSubAccountsWithBlockchainData = async (
    rootAddress: string,
    storedSubAccounts: SubAccount[],
    forceRefresh = false,
  ): Promise<SubAccount[]> => {
    const addresses = await this.fetchOwnedSubAddresses(rootAddress, forceRefresh);

    if (!addresses || !addresses.length) {
      return storedSubAccounts;
    }

    const { types: typesRaw, subs, nonces, orderIndexes } = await this.fetchSubAccountsRaw(addresses, forceRefresh);

    const subAccounts: SubAccount[] = [];

    for (let i = 0; i < subs.length; i++) {
      const bcAddress = subs[i];
      const normalizedBcAddress = this.normalizeAddress(bcAddress);
      const stored = storedSubAccounts.find(
        (acc: SubAccount) => this.normalizeAddress(acc.address) === normalizedBcAddress,
      );

      const typeArr = typesRaw[i] || [];
      const decodedType = Array.isArray(typeArr)
        ? typeArr.map(this.safeDecodeType).join("")
        : this.safeDecodeType(typeArr);
      const isFunding = decodedType.includes("Funding");
      const isTrading = decodedType.includes("CrossTrading");
      const nonceRaw = nonces[i];
      const nonceValue = String(nonceRaw || "0");
      const orderIndex = Number.parseInt(orderIndexes[i], 10);

      if (isFunding) {
        if (stored?.privateKey) {
          subAccounts.push({ ...stored, address: normalizedBcAddress, orderIndex });
        } else {
          subAccounts.push({
            address: normalizedBcAddress,
            privateKey: stored?.privateKey || "",
            publicKey: stored?.publicKey || "",
            type: "funding",
            orderIndex,
            nonce: "0",
          });
        }
      } else if (isTrading) {
        if (stored?.privateKey) {
          subAccounts.push({ ...stored, address: normalizedBcAddress, nonce: nonceValue, orderIndex });
        } else {
          subAccounts.push({
            address: normalizedBcAddress,
            privateKey: stored?.privateKey || "",
            publicKey: stored?.publicKey || "",
            nonce: nonceValue,
            type: "trading",
            orderIndex,
          });
        }
      }
    }

    return subAccounts;
  };

  clearCaches = (address?: string): void => {
    if (address) {
      this.ownedSubAddressesCache.delete(address);
      this.masterSignatureCache.delete(address);
    } else {
      this.ownedSubAddressesCache.clear();
      this.masterSignatureCache.clear();
    }
    this.subAccountsRawCache.clear();
  };

  private viewWithRetry = async <T>(payload: any, retries = 3, onFailure?: () => void | Promise<void>): Promise<T> => {
    let data: T = [] as T;

    while (retries > 0) {
      try {
        data = (await aptosClient.view({ payload })) as T;
        return data;
      } catch {
        retries--;
      }
      await wait(1);
    }
    await onFailure?.();
    throw new Error("Failed to view with retry");
  };
}

export const subAccountService = new SubAccountService();
